// src/pages/FormProduto.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Stack, Alert, Grid,
  RadioGroup, Radio, InputAdornment, Tooltip, Chip, Divider, FormHelperText, CircularProgress, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import {
  getProductById,
  createProduct,
  updateProduct,
  getAllCategories,
  getSubCategoriesByCategory,
  getAllTributaryGroups,
  getAllBrands,
  getAllSuppliers,
  getEstabelecimentos,
  getConfiguracao,
  getLocaisEstoque,
  reajusteSaldoEstoque,
} from '../services/api';
import { getAuthPermissoes } from '../services/authStorage';
import { getApiErrorMessage } from '../utils/apiError';
import CategoryFormDialog from './CategoryFormDialog';
import SubCategoryFormDialog from './SubCategoryFormDialog';
import BrandFormDialog from './BrandFormDialog';
import {
  formatBRLFromNumber,
  formatCurrencyInputBR,
  parseBRLToNumber,
} from '../utils/decimalBr';
import useModulosLicenca from '../hooks/useModulosLicenca';

function modoMargemDaFilial(filiais, estabId) {
  const filial = (filiais || []).find((item) => Number(item.id) === Number(estabId));
  return Number(filial?.tipoCalculoMargem) === 1 ? 'markup' : 'margem';
}

function calcPercentualLucro(preco, custo, modo) {
  if (preco == null || custo == null || custo <= 0 || preco <= 0) return null;
  const bruto = modo === 'markup'
    ? ((preco - custo) / custo) * 100
    : ((preco - custo) / preco) * 100;
  const teto = modo === 'markup' ? 9999 : 99.9999;
  return Math.min(Math.max(bruto, -999), teto);
}

function calcPrecoPorPercentual(percentual, custo, modo) {
  if (custo == null) return null;
  if (modo === 'markup') {
    const preco = custo * (1 + percentual / 100);
    return preco > 0 ? preco : null;
  }
  const margem = percentual / 100;
  if (margem >= 1) return null;
  const preco = custo / (1 - margem);
  return preco > 0 ? preco : null;
}

const sectionCardSx = {
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  backgroundColor: 'background.paper',
};

const sectionTitleSx = (theme) => ({
  px: 2,
  py: 1.25,
  fontSize: 14,
  fontWeight: 800,
  letterSpacing: 0.2,
  textTransform: 'uppercase',
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
  backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
});

const MAX_IMAGE_BYTES = 3 * 1024 * 1024;

function fmtQtd(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

function linhasDaFilial(estoques, estabId) {
  const eid = Number(estabId);
  return (estoques || []).filter((e) => Number(e.estabelecimentoId) === eid);
}

function linhaLimitesFilial(estoques, locais, estabId) {
  const daFilial = linhasDaFilial(estoques, estabId);
  const principal = (locais || []).find(
    (l) => Number(l.estabelecimentoId) === Number(estabId) && l.principal,
  );
  if (principal) {
    const row = daFilial.find((e) => Number(e.localEstoqueId) === Number(principal.id));
    if (row) return row;
  }
  return daFilial[0];
}

function Section({ title, children, sx }) {
  return (
    <Box sx={{ ...sectionCardSx, ...sx }}>
      <Box sx={sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

function SelectWithAdd({
  label,
  value,
  onChange,
  options,
  onAdd,
  disabled = false,
  required = false,
  error = false,
  helperText = ' ',
  emptyLabel = 'Selecione',
}) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      <FormControl fullWidth required={required} error={error} disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={value} onChange={onChange}>
          <MenuItem value="">{emptyLabel}</MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.id} value={String(opt.id)}>{opt.descricao}</MenuItem>
          ))}
        </Select>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
      {onAdd ? (
        <Tooltip title={`Nova ${label.toLowerCase()}`}>
          <IconButton onClick={onAdd} disabled={disabled} sx={{ mt: 0.75 }} aria-label={`Adicionar ${label}`}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

function FormProduto({
  isEditMode = false,
  productId = null,
  duplicateFromId = null,
  onSaved,
  onCancel,
  embedded = false,
  estabelecimentoId = 1,
  tabelaPrecoIdPadrao = 1,
}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAtivo } = useModulosLicenca();
  const multiEstoque = isAtivo('ADD_MULTI_ESTOQUE');

  const id = productId ?? params.id;
  const editing = isEditMode || !!productId;
  const duplicateId = duplicateFromId ?? (editing ? null : searchParams.get('duplicateId'));

  // Campos básicos
  const [descricao, setDescricao] = useState('');
  const [codigo, setCodigo] = useState('');
  const [ean, setEan] = useState('');
  const [referencia, setReferencia] = useState('');
  const [codigosAdicionais, setCodigosAdicionais] = useState([]);
  const [unidade, setUnidade] = useState('UN');
  const [ativo, setAtivo] = useState(true);
  const [ativoNaFilial, setAtivoNaFilial] = useState(true);
  const [balanca, setBalanca] = useState(0);

  // Classificação
  const [categoriaId, setCategoriaId] = useState('');
  const [subCategoriaId, setSubCategoriaId] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [filteredSubCategorias, setFilteredSubCategorias] = useState([]);

  // Marca
  const [marcaId, setMarcaId] = useState('');
  const [marcas, setMarcas] = useState([]);
  const [fornecedorId, setFornecedorId] = useState('');
  const [fornecedores, setFornecedores] = useState([]);

  // Tributação / outros
  const [grupoTributarioId, setGrupoTributarioId] = useState('');
  const [ncm, setNcm] = useState('');
  const [cest, setCest] = useState('');
  const [combustivel, setCombustivel] = useState(false);
  const [obrigaIdentificacaoCliente, setObrigaIdentificacaoCliente] = useState(false);
  const [percentualComissao, setPercentualComissao] = useState('0');
  const [codigoAnp, setCodigoAnp] = useState('');
  const [descricaoAnp, setDescricaoAnp] = useState('');
  const [ufConsumo, setUfConsumo] = useState('');
  const [validade, setValidade] = useState('');
  const [qtdAtacado, setQtdAtacado] = useState('');

  // Preços (R$)
  const [precoPorFilial, setPrecoPorFilial] = useState(false);
  const [filiais, setFiliais] = useState([]);
  const filiaisRef = useRef([]);
  const [modoMargem, setModoMargem] = useState('margem');
  const [estabPrecoId, setEstabPrecoId] = useState(
    Number(estabelecimentoId) > 0 ? Number(estabelecimentoId) : '',
  );
  const [precosCache, setPrecosCache] = useState([]);
  const [estoquesCache, setEstoquesCache] = useState([]);
  const [locaisEstoque, setLocaisEstoque] = useState([]);
  const [dlgEstoque, setDlgEstoque] = useState(false);
  const [filtroFilialEstoque, setFiltroFilialEstoque] = useState('');
  // Reajuste rápido de saldo (permissão reajusteEstoque)
  const [dlgReajuste, setDlgReajuste] = useState(false);
  const [reajusteQtd, setReajusteQtd] = useState('');
  const [reajusteLocalId, setReajusteLocalId] = useState('');
  const [reajusteObs, setReajusteObs] = useState('');
  const [reajusteBusy, setReajusteBusy] = useState(false);
  const [reajusteErro, setReajusteErro] = useState('');
  const podeReajustarSaldo = useMemo(() => {
    try {
      return Boolean(getAuthPermissoes()?.reajusteEstoque);
    } catch {
      return false;
    }
  }, []);
  const [valorCusto, setValorCusto] = useState('');
  const [valorVarejo, setValorVarejo] = useState('');
  const [valorAtacado, setValorAtacado] = useState('');
  const [valorPromocional, setValorPromocional] = useState('');

  // Margens (%)
  const [margemVarejo, setMargemVarejo] = useState('');
  const [margemAtacado, setMargemAtacado] = useState('');
  const [margemPromocional, setMargemPromocional] = useState('');

  // Imagem base64
  const [imagemBase64, setImagemBase64] = useState(null);

  // Estoque
  const [estoqueAtual, setEstoqueAtual] = useState(0); // apenas visual
  const [estoqueMinimo, setEstoqueMinimo] = useState(''); // << NOVO
  const [estoqueIdeal, setEstoqueIdeal] = useState('');   // << NOVO
  const [estoqueMaximo, setEstoqueMaximo] = useState(''); // << NOVO

  // Datas
  const [dataCadastro, setDataCadastro] = useState(null);
  const [dataUpdate, setDataUpdate] = useState(null);

  // UI
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [duplicatingFrom, setDuplicatingFrom] = useState(null);
  const [gruposTributarios, setGruposTributarios] = useState([]);

  const [dlgCategoria, setDlgCategoria] = useState(false);
  const [dlgSubcategoria, setDlgSubcategoria] = useState(false);
  const [dlgMarca, setDlgMarca] = useState(false);

  // Moeda BRL: formatCurrencyInputBR / formatBRLFromNumber / parseBRLToNumber (utils/decimalBr)
  const toIntOrZero = (v) => {
    const n = parseInt(String(v).replace(/\D/g, ''), 10);
    return Number.isFinite(n) ? n : 0;
  };
  const toNumberOrNull = parseBRLToNumber;

  // decimais simples (para estoques)
  const toDecimal = (v) => {                            // << NOVO
    if (v === '' || v === null || v === undefined) return 0;
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  // Percent helpers
  const parsePercent = (s) => {
    if (s === '' || s === null || s === undefined) return null;
    const n = Number(String(s).replace(/[^\d.,-]/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };
  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  // ********** Regras de custo/margem/preço **********

  // Conversões para payload dos preços
  const precosPayload = useMemo(() => {
    const estabId = Number(estabPrecoId) || Number(estabelecimentoId) || 1;
    const base = { tabelaPrecoId: tabelaPrecoIdPadrao, estabelecimentoId: estabId };
    const items = [];
    const pushIfNumber = (tipo, valorStr) => {
      const valor = toNumberOrNull(valorStr);
      if (valor !== null) items.push({ ...base, tipo, valor });
    };
    pushIfNumber(0, valorVarejo);
    pushIfNumber(1, valorAtacado);
    pushIfNumber(2, valorPromocional);
    pushIfNumber(3, valorCusto);
    return items;
  }, [tabelaPrecoIdPadrao, estabelecimentoId, estabPrecoId, valorVarejo, valorAtacado, valorPromocional, valorCusto, toNumberOrNull]);

  const aplicarPrecosNaTela = (precos, estabId, porFilial) => {
    const eid = Number(estabId);
    let filtered = (precos || []).filter((p) => Number(p.estabelecimentoId) === eid);
    if (filtered.length === 0 && !porFilial && (precos || []).length > 0) {
      const anyId = Number(precos[0].estabelecimentoId);
      filtered = precos.filter((p) => Number(p.estabelecimentoId) === anyId);
    }
    const byTipo = (t) => filtered.find((p) => p.tipo === t)?.valor ?? '';

    const custoNum = byTipo(3) !== '' ? Number(byTipo(3)) : null;
    const varejoNum = byTipo(0) !== '' ? Number(byTipo(0)) : null;
    const atacadoNum = byTipo(1) !== '' ? Number(byTipo(1)) : null;
    const promoNum = byTipo(2) !== '' ? Number(byTipo(2)) : null;

    setValorCusto(custoNum != null ? formatBRLFromNumber(custoNum) : '');
    setValorVarejo(varejoNum != null ? formatBRLFromNumber(varejoNum) : '');
    setValorAtacado(atacadoNum != null ? formatBRLFromNumber(atacadoNum) : '');
    setValorPromocional(promoNum != null ? formatBRLFromNumber(promoNum) : '');

    const modo = modoMargemDaFilial(filiaisRef.current, eid);
    setModoMargem(modo);
    setMargemVarejo(calcPercentualLucro(varejoNum, custoNum, modo)?.toFixed(2) ?? '');
    setMargemAtacado(calcPercentualLucro(atacadoNum, custoNum, modo)?.toFixed(2) ?? '');
    setMargemPromocional(calcPercentualLucro(promoNum, custoNum, modo)?.toFixed(2) ?? '');
  };

  const aplicarEstoqueNaTela = (estoques, estabId) => {
    const eid = Number(estabId);
    const daFilial = linhasDaFilial(estoques, eid);
    const saldo = daFilial.reduce((soma, e) => soma + (Number(e.saldo) || 0), 0);
    const row = linhaLimitesFilial(estoques, locaisEstoque, eid);
    setEstoqueAtual(saldo);
    setEstoqueMinimo(row?.estoqueMinimo?.toString() ?? '');
    setEstoqueIdeal(row?.estoqueIdeal?.toString() ?? '');
    setEstoqueMaximo(row?.estoqueMaximo?.toString() ?? '');
    setAtivoNaFilial(row?.ativo !== false);
  };

  useEffect(() => {
    let ativo = true;
    getLocaisEstoque()
      .then(({ data }) => {
        if (ativo) setLocaisEstoque(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (ativo) setLocaisEstoque([]);
      });
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    if (!locaisEstoque.length) return;
    aplicarEstoqueNaTela(estoquesCache, estabPrecoId);
    // Quando a lista de locais chega, o saldo passa a ser a soma da filial.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locaisEstoque]);

  const persistirLimitesNoCache = (estabId) => {
    const eid = Number(estabId);
    if (!eid) return;
    const patch = {
      estoqueMinimo: toDecimal(estoqueMinimo),
      estoqueIdeal: toDecimal(estoqueIdeal),
      estoqueMaximo: toDecimal(estoqueMaximo),
      ativo: ativoNaFilial !== false,
    };
    setEstoquesCache((prev) => {
      const lista = [...(prev || [])];
      const principal = locaisEstoque.find(
        (l) => Number(l.estabelecimentoId) === eid && l.principal,
      );
      const idx = principal
        ? lista.findIndex((e) => Number(e.estabelecimentoId) === eid
          && Number(e.localEstoqueId) === Number(principal.id))
        : lista.findIndex((e) => Number(e.estabelecimentoId) === eid);
      if (idx >= 0) {
        lista[idx] = { ...lista[idx], ...patch };
      } else {
        lista.push({
          estabelecimentoId: eid,
          localEstoqueId: principal?.id,
          saldo: 0,
          ...patch,
        });
      }
      return lista;
    });
  };

  // Arquivo → base64 (sem prefixo)
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const s = typeof reader.result === 'string' ? reader.result : '';
        resolve(s.split(',')[1] || null);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Carrega combos + popula
  useEffect(() => {
    const fetchData = async () => {
      const [categoriasResponse, gruposTributariosResponse, marcasResponse, estabsResponse] = await Promise.all([
        getAllCategories({ indice: 1, qtde: 200 }),
        getAllTributaryGroups({ indice: 1, qtde: 200 }),
        getAllBrands({ indice: 1, qtde: 200 }),
        getEstabelecimentos(),
      ]);
      setCategorias(categoriasResponse.data.items || []);
      setGruposTributarios(gruposTributariosResponse.data.items || []);
      setMarcas(marcasResponse.data.items || []);

      try {
        const fornecedoresResponse = await getAllSuppliers({ indice: 1, qtde: 200 });
        const itens = fornecedoresResponse.data?.items || fornecedoresResponse.data || [];
        setFornecedores(Array.isArray(itens) ? itens : []);
      } catch {
        setFornecedores([]);
      }

      const raw = estabsResponse.data;
      const lista = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      const mapeadas = lista.map((e) => ({
        id: e.id,
        nome: e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`,
        tipoCalculoMargem: Number(e.tipoCalculoMargem) === 1 ? 1 : 0,
      }));
      filiaisRef.current = mapeadas;
      setFiliais(mapeadas);

      const propId = Number(estabelecimentoId);
      const firstId = propId > 0 ? propId : (lista[0]?.id ?? 1);
      let politica = false;
      try {
        const { data: cfg } = await getConfiguracao(firstId);
        politica = Boolean(cfg.precoPorFilial);
      } catch {
        politica = false;
      }
      setPrecoPorFilial(politica);
      setEstabPrecoId(firstId);
      return { firstId, politica };
    };

    const fillFromProduto = (produto, { asCopy = false, estabId, porFilial } = {}) => {
      setDescricao(asCopy ? `${produto.descricao || ''} (CÓPIA)` : (produto.descricao || ''));
      setCodigo(asCopy ? '' : (produto.codigo || ''));
      setEan(asCopy ? '' : (produto.ean || ''));
      setReferencia(asCopy ? '' : (produto.referencia || ''));
      setCodigosAdicionais(
        asCopy
          ? []
          : (produto.codigosAdicionais || []).map((c) => ({
              id: c.id || 0,
              codigo: c.codigo || '',
              quantidade: c.quantidade ?? 1,
              descricao: c.descricao || '',
            }))
      );
      setUnidade(String(produto.unidade || 'UN').toUpperCase());
      setAtivo(asCopy ? true : !!produto.ativo);
      setBalanca(Number(produto.balanca) || 0);

      setCategoriaId(produto.categoriaId ? String(produto.categoriaId) : '');
      setSubCategoriaId(produto.subCategoriaId ? String(produto.subCategoriaId) : '');
      setGrupoTributarioId(produto.grupoTributarioId ? String(produto.grupoTributarioId) : '');

      setMarcaId(produto.marcaId ? String(produto.marcaId) : '');
      setFornecedorId(produto.fornecedorId ? String(produto.fornecedorId) : '');

      setNcm(produto.ncm || '');
      setCest(produto.cest || '');
      setCombustivel(Boolean(produto.combustivel));
      setObrigaIdentificacaoCliente(Boolean(produto.obrigaIdentificacaoCliente));
      setPercentualComissao(String(produto.percentualComissao ?? 0));
      setCodigoAnp(produto.codigoAnp || '');
      setDescricaoAnp(produto.descricaoAnp || '');
      setUfConsumo((produto.ufConsumo || '').toUpperCase());
      setValidade(produto.validade?.toString() || '');
      setQtdAtacado(produto.qtdAtacado?.toString() || '');

      setDataCadastro(produto.dataCadastro || null);
      setDataUpdate(produto.dataUpdate || null);

      const precos = asCopy ? [] : (produto.precos || []);
      setPrecosCache(precos);
      aplicarPrecosNaTela(precos, estabId, porFilial);

      const estoques = asCopy ? [] : (produto.estoques || []);
      setEstoquesCache(estoques);
      if (asCopy) setEstoqueAtual(0);
      else aplicarEstoqueNaTela(estoques, estabId);

      setImagemBase64(produto.imagem || null);
    };

    const fetchForEdit = async (estabId, porFilial) => {
      if (!editing) return;
      const { data: produto } = await getProductById(id);
      fillFromProduto(produto, { asCopy: false, estabId, porFilial });
    };

    const fetchForDuplicate = async (estabId, porFilial) => {
      if (editing || !duplicateId) return;
      const { data: produtoOrigem } = await getProductById(duplicateId);
      setDuplicatingFrom(produtoOrigem.id);
      fillFromProduto(produtoOrigem, { asCopy: true, estabId, porFilial });
    };

    (async () => {
      setLoading(true);
      try {
        const { firstId, politica } = await fetchData();
        await fetchForEdit(firstId, politica);
        await fetchForDuplicate(firstId, politica);
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err, 'Erro ao carregar os dados do produto.'));
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editing, duplicateId]);

  // Subcategorias do grupo escolhido (filtradas no servidor)
  useEffect(() => {
    if (!categoriaId) {
      setFilteredSubCategorias([]);
      setSubCategoriaId('');
      return undefined;
    }

    let cancelado = false;
    (async () => {
      try {
        const { data } = await getSubCategoriesByCategory(categoriaId);
        if (!cancelado) setFilteredSubCategorias(Array.isArray(data) ? data : (data?.items ?? []));
      } catch (err) {
        if (!cancelado) setErrorMessage(getApiErrorMessage(err, 'Erro ao carregar as subcategorias.'));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [categoriaId]);

  const recarregarCategorias = async (selecionarId) => {
    const { data } = await getAllCategories({ indice: 1, qtde: 200 });
    const lista = data.items || [];
    setCategorias(lista);
    if (selecionarId) {
      setCategoriaId(String(selecionarId));
      setSubCategoriaId('');
    }
    return lista;
  };

  const recarregarSubcategorias = async (catId, selecionarId) => {
    if (!catId) {
      setFilteredSubCategorias([]);
      return [];
    }
    const { data } = await getSubCategoriesByCategory(catId);
    const lista = Array.isArray(data) ? data : (data?.items ?? []);
    setFilteredSubCategorias(lista);
    if (selecionarId) setSubCategoriaId(String(selecionarId));
    return lista;
  };

  const recarregarMarcas = async (selecionarId) => {
    const { data } = await getAllBrands({ indice: 1, qtde: 200 });
    const lista = data.items || [];
    setMarcas(lista);
    if (selecionarId) setMarcaId(String(selecionarId));
    return lista;
  };

  // ======= Handlers: custo/margem/preço =======
  const onChangeCusto = (str) => {
    const formatted = formatCurrencyInputBR(str);
    setValorCusto(formatted);

    const custo = toNumberOrNull(formatted);
    const v = toNumberOrNull(valorVarejo);
    const a = toNumberOrNull(valorAtacado);
    const p = toNumberOrNull(valorPromocional);

    setMargemVarejo(calcPercentualLucro(v, custo, modoMargem)?.toFixed(2) ?? '');
    setMargemAtacado(calcPercentualLucro(a, custo, modoMargem)?.toFixed(2) ?? '');
    setMargemPromocional(calcPercentualLucro(p, custo, modoMargem)?.toFixed(2) ?? '');
  };

  const onChangePreco = (qual, str) => {
    const formatted = formatCurrencyInputBR(str);
    const custo = toNumberOrNull(valorCusto);

    if (qual === 'varejo') setValorVarejo(formatted);
    if (qual === 'atacado') setValorAtacado(formatted);
    if (qual === 'promo') setValorPromocional(formatted);

    const preco = toNumberOrNull(formatted);
    const m = calcPercentualLucro(preco, custo, modoMargem)?.toFixed(2) ?? '';

    if (qual === 'varejo') setMargemVarejo(m);
    if (qual === 'atacado') setMargemAtacado(m);
    if (qual === 'promo') setMargemPromocional(m);
  };

  const onChangeMargem = (qual, str) => {
    let pct = parsePercent(str);
    if (pct === null) pct = 0;
    pct = clamp(pct, -999, modoMargem === 'markup' ? 9999 : 99.9999);

    const custo = toNumberOrNull(valorCusto);
    if (custo == null || custo <= 0) {
      if (qual === 'varejo') setMargemVarejo(String(pct));
      if (qual === 'atacado') setMargemAtacado(String(pct));
      if (qual === 'promo') setMargemPromocional(String(pct));
      return;
    }

    const novoPreco = calcPrecoPorPercentual(pct, custo, modoMargem);

    if (qual === 'varejo') {
      setMargemVarejo(String(pct));
      setValorVarejo(novoPreco != null ? formatBRLFromNumber(novoPreco) : valorVarejo);
    }
    if (qual === 'atacado') {
      setMargemAtacado(String(pct));
      setValorAtacado(novoPreco != null ? formatBRLFromNumber(novoPreco) : valorAtacado);
    }
    if (qual === 'promo') {
      setMargemPromocional(String(pct));
      setValorPromocional(novoPreco != null ? formatBRLFromNumber(novoPreco) : valorPromocional);
    }
  };

  const fechar = useCallback(() => {
    if (onCancel) onCancel();
    else navigate('/products');
  }, [onCancel, navigate]);

  // Hotkeys: F10 grava; ESC volta (no diálogo o próprio MUI trata o ESC)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'F10') {
        e.preventDefault();
        document.getElementById('btn-save')?.click();
      } else if (e.key === 'Escape' && !embedded) {
        e.preventDefault();
        fechar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embedded, fechar]);

  const validar = () => {
    const erros = {};
    if (!descricao?.trim()) erros.descricao = 'Informe a descrição.';
    if (!categoriaId) erros.categoriaId = 'Selecione a categoria.';
    if (!subCategoriaId) erros.subCategoriaId = 'Selecione a subcategoria.';
    if (!unidade) erros.unidade = 'Selecione a unidade.';
    if (!grupoTributarioId) erros.grupoTributarioId = 'Selecione o grupo tributário.';
    if (parseBRLToNumber(valorVarejo) === null) erros.valorVarejo = 'Informe o preço de varejo.';
    if (combustivel) {
      const anp = String(codigoAnp || '').replace(/\D/g, '');
      if (anp.length < 6) erros.codigoAnp = 'Informe o código ANP (cProdANP).';
      if (ufConsumo && !/^[A-Z]{2}$/.test(ufConsumo.trim().toUpperCase())) {
        erros.ufConsumo = 'UF inválida (ex.: SP).';
      }
    }
    return erros;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    const erros = validar();
    setFieldErrors(erros);
    if (Object.keys(erros).length) {
      setErrorMessage('Revise os campos destacados antes de gravar.');
      return;
    }

    setSaving(true);
    try {
      const estabIdLimites = Number(estabPrecoId) || Number(estabelecimentoId) || 1;
      persistirLimitesNoCache(estabIdLimites);

      const payload = {
        estabelecimentoId: estabIdLimites,
        ativo,
        ativoNaFilial: ativoNaFilial !== false,
        balanca: Number(balanca) || 0,
        ean: ean || null,
        referencia: referencia?.trim() || null,
        codigosAdicionais: (codigosAdicionais || [])
          .filter((c) => String(c.codigo || '').trim())
          .map((c) => ({
            id: Number(c.id) || 0,
            codigo: String(c.codigo).trim(),
            quantidade: Number(String(c.quantidade).replace(',', '.')) || 1,
            descricao: String(c.descricao || '').trim(),
          })),
        descricao: descricao?.trim(),
        unidade,
        categoriaId: Number(categoriaId),
        subCategoriaId: Number(subCategoriaId),
        marcaId: marcaId ? Number(marcaId) : null,
        fornecedorId: fornecedorId ? Number(fornecedorId) : null,
        qtdAtacado: toIntOrZero(qtdAtacado),
        grupoTributarioId: grupoTributarioId ? Number(grupoTributarioId) : null,
        ncm: ncm || null,
        cest: cest || null,
        combustivel: Boolean(combustivel),
        obrigaIdentificacaoCliente: Boolean(obrigaIdentificacaoCliente),
        percentualComissao: Number(String(percentualComissao || 0).replace(',', '.')),
        codigoAnp: combustivel ? String(codigoAnp || '').replace(/\D/g, '') || null : null,
        descricaoAnp: combustivel ? (descricaoAnp?.trim() || null) : null,
        ufConsumo: combustivel
          ? (ufConsumo?.trim().toUpperCase() || null)
          : null,
        validade: toIntOrZero(validade),
        imagem: imagemBase64 ?? null,
        // Estoques
        estoqueMinimo: toDecimal(estoqueMinimo),                            // << NOVO
        estoqueIdeal:  toDecimal(estoqueIdeal),                             // << NOVO
        estoqueMaximo: toDecimal(estoqueMaximo),                            // << NOVO
        precos: precosPayload,
      };

      if (editing) {
        payload.codigo = codigo || null;
        await updateProduct(id, payload);
      } else {
        await createProduct(payload);
      }

      if (onSaved) onSaved(editing ? 'Produto atualizado' : 'Produto cadastrado');
      else navigate('/products');
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Erro ao salvar o produto.'));
    } finally {
      setSaving(false);
    }
  };

  const isCreateMode = !editing;

  const locaisReajuste = useMemo(() => {
    const eid = Number(estabPrecoId);
    return (locaisEstoque || []).filter(
      (l) => l.ativo !== false && (!eid || Number(l.estabelecimentoId) === eid),
    );
  }, [locaisEstoque, estabPrecoId]);

  const saldoParaReajuste = (localId) => {
    const daFilial = linhasDaFilial(estoquesCache, estabPrecoId);
    if (!localId) return daFilial.reduce((s, e) => s + (Number(e.saldo) || 0), 0);
    return daFilial
      .filter((e) => Number(e.localEstoqueId) === Number(localId))
      .reduce((s, e) => s + (Number(e.saldo) || 0), 0);
  };

  const abrirReajuste = () => {
    const principal = locaisReajuste.find((l) => l.principal) || locaisReajuste[0];
    const localInicial = multiEstoque && principal ? String(principal.id) : '';
    setReajusteLocalId(localInicial);
    setReajusteQtd(String(saldoParaReajuste(localInicial)));
    setReajusteObs('');
    setReajusteErro('');
    setDlgReajuste(true);
  };

  const confirmarReajuste = async () => {
    const qtd = Number(String(reajusteQtd).replace(',', '.'));
    if (!Number.isFinite(qtd) || qtd < 0) {
      setReajusteErro('Informe a nova quantidade (maior ou igual a zero).');
      return;
    }
    const eid = Number(estabPrecoId) || Number(estabelecimentoId) || 0;
    if (!eid) {
      setReajusteErro('Selecione a filial.');
      return;
    }
    const localId = multiEstoque ? (Number(reajusteLocalId) || 0) : 0;
    setReajusteBusy(true);
    setReajusteErro('');
    try {
      const { data: res } = await reajusteSaldoEstoque({
        produtoId: Number(id),
        quantidade: qtd,
        estabelecimentoId: eid,
        localEstoqueId: localId,
        observacao: reajusteObs.trim() || undefined,
      });
      if ((res?.erros || 0) > 0) {
        setReajusteErro(res?.errosDetalhe?.[0]?.mensagem || 'Não foi possível reajustar o saldo.');
        return;
      }

      // Atualização otimista do saldo na ficha.
      const principal = (locaisEstoque || []).find(
        (l) => Number(l.estabelecimentoId) === eid && l.principal,
      );
      const alvoLocal = localId || Number(principal?.id) || 0;
      let novoCache = (estoquesCache || []).map((e) => (
        Number(e.estabelecimentoId) === eid && Number(e.localEstoqueId) === alvoLocal
          ? { ...e, saldo: qtd }
          : e
      ));
      if (!novoCache.some((e) => Number(e.estabelecimentoId) === eid
        && Number(e.localEstoqueId) === alvoLocal)) {
        novoCache = [...novoCache, {
          estabelecimentoId: eid, localEstoqueId: alvoLocal, saldo: qtd,
        }];
      }

      // Confirma com o saldo real do servidor (mescla só saldos, preservando edições de limites).
      try {
        const { data: produto } = await getProductById(id);
        (produto?.estoques || []).forEach((srv) => {
          const idx = novoCache.findIndex((e) => Number(e.estabelecimentoId) === Number(srv.estabelecimentoId)
            && Number(e.localEstoqueId) === Number(srv.localEstoqueId));
          if (idx >= 0) novoCache[idx] = { ...novoCache[idx], saldo: srv.saldo };
          else novoCache.push(srv);
        });
      } catch {
        // mantém o valor otimista
      }

      setEstoquesCache(novoCache);
      setEstoqueAtual(
        linhasDaFilial(novoCache, eid).reduce((s, e) => s + (Number(e.saldo) || 0), 0),
      );
      setDlgReajuste(false);
    } catch (err) {
      setReajusteErro(getApiErrorMessage(err, 'Não foi possível reajustar o saldo.'));
    } finally {
      setReajusteBusy(false);
    }
  };

  const linhasEstoqueModal = useMemo(() => {
    const eid = Number(filtroFilialEstoque);
    const locais = (locaisEstoque || []).filter(
      (l) => !eid || Number(l.estabelecimentoId) === eid,
    );
    const saldos = (estoquesCache || []).filter(
      (e) => !eid || Number(e.estabelecimentoId) === eid,
    );
    const porLocal = new Map();
    locais.forEach((l) => {
      porLocal.set(Number(l.id), {
        id: l.id,
        filialId: l.estabelecimentoId,
        filialNome: filiais.find((f) => Number(f.id) === Number(l.estabelecimentoId))?.nome || '',
        nome: l.nome || 'Estoque principal',
        principal: Boolean(l.principal),
        saldo: 0,
      });
    });
    saldos.forEach((e) => {
      const id = Number(e.localEstoqueId);
      const qtd = Number(e.saldo) || 0;
      const atual = porLocal.get(id);
      if (atual) {
        atual.saldo += qtd;
        return;
      }
      porLocal.set(id, {
        id,
        filialId: e.estabelecimentoId,
        filialNome: filiais.find((f) => Number(f.id) === Number(e.estabelecimentoId))?.nome || '',
        nome: 'Local',
        principal: false,
        saldo: qtd,
      });
    });
    const lista = [...porLocal.values()].sort((a, b) => {
      if (a.principal !== b.principal) return a.principal ? -1 : 1;
      const filial = String(a.filialNome).localeCompare(String(b.filialNome), 'pt-BR');
      if (filial) return filial;
      return String(a.nome).localeCompare(String(b.nome), 'pt-BR');
    });
    const total = lista.reduce((soma, linha) => soma + linha.saldo, 0);
    return { lista, total };
  }, [filtroFilialEstoque, locaisEstoque, estoquesCache, filiais]);

  return (
    <Box
      component="form"
      onSubmit={handleSave}
      noValidate
      sx={{ p: embedded ? 0 : 2, maxWidth: embedded ? 'none' : 1400, mx: 'auto' }}
    >
      {!embedded && (
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={900}>
            {isCreateMode ? 'Novo produto' : 'Ficha do produto'}
          </Typography>
          <Typography color="text.secondary" noWrap>
            {descricao || 'Preencha os dados abaixo para cadastrar o produto.'}
          </Typography>
        </Stack>
      )}

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Carregando dados...</Typography>
        </Stack>
      )}

      {isCreateMode && duplicateId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Criando uma <strong>cópia</strong> do produto #{duplicatingFrom}. O código será gerado ao salvar.
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Coluna esquerda */}
        <Grid item xs={12} md={8} lg={9}>
          <Section title="Informações Gerais" sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Tooltip
                  title={isCreateMode ? 'Gerado automaticamente no salvamento' : 'Você pode editar o código'}
                >
                  <TextField
                    label="Código"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    fullWidth
                    disabled={isCreateMode}
                    InputProps={{
                      endAdornment: isCreateMode ? (
                        <InputAdornment position="end">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ) : null,
                    }}
                    helperText={isCreateMode ? 'Será gerado automaticamente.' : 'Editável.'}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Barras (EAN)"
                  value={ean}
                  onChange={(e) => setEan(e.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 14 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Referência"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value.slice(0, 60))}
                  fullWidth
                  inputProps={{ maxLength: 60 }}
                  helperText="Cód. fabricante / referência interna"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth error={!!fieldErrors.unidade}>
                  <InputLabel>UN</InputLabel>
                  <Select
                    label="UN"
                    value={unidade}
                    onChange={(e) => setUnidade(String(e.target.value).toUpperCase())}
                  >
                    <MenuItem value="UN">UN (Unidade)</MenuItem>
                    <MenuItem value="KG">KG (Quilo)</MenuItem>
                    <MenuItem value="LT">LT (Litro)</MenuItem>
                    <MenuItem value="MT">MT (Metro)</MenuItem>
                    <MenuItem value="PC">PC (Peça)</MenuItem>
                    <MenuItem value="CX">CX (Caixa)</MenuItem>
                  </Select>
                  {fieldErrors.unidade && <FormHelperText>{fieldErrors.unidade}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                  Códigos adicionais (fardo, caixa…)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Cada código extra entra no estoque e na venda como a quantidade de unidades do produto.
                </Typography>
                <Stack spacing={1}>
                  {codigosAdicionais.map((linha, idx) => (
                    <Stack key={linha.id || `novo-${idx}`} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        size="small"
                        label="Código / EAN"
                        value={linha.codigo}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCodigosAdicionais((prev) => prev.map((c, i) => (i === idx ? { ...c, codigo: v } : c)));
                        }}
                        sx={{ flex: 1.4 }}
                      />
                      <TextField
                        size="small"
                        label="Qtd em unidades"
                        value={linha.quantidade}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCodigosAdicionais((prev) => prev.map((c, i) => (i === idx ? { ...c, quantidade: v } : c)));
                        }}
                        sx={{ width: { sm: 140 } }}
                      />
                      <TextField
                        size="small"
                        label="Descrição"
                        placeholder="FARDO, CX 12…"
                        value={linha.descricao}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCodigosAdicionais((prev) => prev.map((c, i) => (i === idx ? { ...c, descricao: v } : c)));
                        }}
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        color="error"
                        onClick={() => setCodigosAdicionais((prev) => prev.filter((_, i) => i !== idx))}
                        aria-label="Remover código"
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCodigosAdicionais((prev) => [...prev, { id: 0, codigo: '', quantidade: 1, descricao: '' }])}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Adicionar código
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descrição"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  fullWidth
                  required
                  autoFocus={isCreateMode}
                  error={!!fieldErrors.descricao}
                  helperText={fieldErrors.descricao || ' '}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectWithAdd
                  label="Categoria"
                  value={categoriaId}
                  onChange={(e) => {
                    setCategoriaId(e.target.value);
                    setSubCategoriaId('');
                  }}
                  options={categorias}
                  onAdd={() => setDlgCategoria(true)}
                  required
                  error={!!fieldErrors.categoriaId}
                  helperText={fieldErrors.categoriaId || ' '}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectWithAdd
                  label="Subcategoria"
                  value={subCategoriaId}
                  onChange={(e) => setSubCategoriaId(e.target.value)}
                  options={filteredSubCategorias}
                  onAdd={() => setDlgSubcategoria(true)}
                  disabled={!categoriaId}
                  required
                  error={!!fieldErrors.subCategoriaId}
                  helperText={
                    fieldErrors.subCategoriaId
                      || (!categoriaId ? 'Selecione uma categoria primeiro.' : ' ')
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SelectWithAdd
                  label="Marca"
                  value={marcaId}
                  onChange={(e) => setMarcaId(e.target.value)}
                  options={marcas}
                  onAdd={() => setDlgMarca(true)}
                  emptyLabel="Sem marca"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <SelectWithAdd
                  label="Fornecedor padrão"
                  value={fornecedorId}
                  onChange={(e) => setFornecedorId(e.target.value)}
                  options={fornecedores.map((f) => ({
                    id: f.id,
                    descricao: f.nomeFantasia || f.razaoSocial || `Fornecedor ${f.id}`,
                  }))}
                  emptyLabel="Sem fornecedor"
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Dados Fiscais" sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!fieldErrors.grupoTributarioId}>
                  <InputLabel>Grupo Tributário</InputLabel>
                  <Select
                    label="Grupo Tributário"
                    value={grupoTributarioId}
                    onChange={(e) => setGrupoTributarioId(e.target.value)}
                  >
                    <MenuItem value="">Selecione</MenuItem>
                    {gruposTributarios.map((g) => (
                      <MenuItem key={g.id} value={String(g.id)}>{g.descricao}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{fieldErrors.grupoTributarioId || ' '}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="NCM"
                  value={ncm}
                  onChange={(e) => setNcm(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  fullWidth
                  inputProps={{ maxLength: 8 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="CEST"
                  value={cest}
                  onChange={(e) => setCest(e.target.value.replace(/\D/g, '').slice(0, 7))}
                  fullWidth
                  inputProps={{ maxLength: 7 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={combustivel}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setCombustivel(on);
                        if (!on) {
                          setCodigoAnp('');
                          setDescricaoAnp('');
                          setUfConsumo('');
                        }
                      }}
                    />
                  }
                  label="Produto combustível (NFC-e / grupo ANP)"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={obrigaIdentificacaoCliente}
                      onChange={(e) => setObrigaIdentificacaoCliente(e.target.checked)}
                    />
                  }
                  label="Venda obriga identificação do cliente (CPF/cadastro) — inclusive no autoatendimento"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="% Comissão"
                  type="number"
                  value={percentualComissao}
                  onChange={(e) => setPercentualComissao(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: '0.01' }}
                  helperText="Usado quando a empresa calcula comissão por produto"
                />
              </Grid>
              {combustivel && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Código ANP (cProdANP)"
                      value={codigoAnp}
                      onChange={(e) => setCodigoAnp(e.target.value.replace(/\D/g, '').slice(0, 9))}
                      error={!!fieldErrors.codigoAnp}
                      helperText={fieldErrors.codigoAnp || 'Tabela oficial da ANP (ex.: 320101001)'}
                      fullWidth
                      required
                      inputProps={{ maxLength: 9 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Descrição ANP"
                      value={descricaoAnp}
                      onChange={(e) => setDescricaoAnp(e.target.value.slice(0, 120))}
                      helperText="Opcional. Se vazio, usa a descrição do produto."
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="UF consumo"
                      value={ufConsumo}
                      onChange={(e) => setUfConsumo(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 2))}
                      error={!!fieldErrors.ufConsumo}
                      helperText={fieldErrors.ufConsumo || 'Vazio = UF do estabelecimento'}
                      fullWidth
                      inputProps={{ maxLength: 2 }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Section>

          <Section title="Balança" sx={{ mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <RadioGroup
                  row
                  value={String(balanca)}
                  onChange={(e) => setBalanca(Number(e.target.value))}
                >
                  {/* 0=Nenhuma, 1=Checkout, 2=Carga, 3=Ambos */}
                  <FormControlLabel value="1" control={<Radio />} label="Checkout" />
                  <FormControlLabel value="2" control={<Radio />} label="Carga" />
                  <FormControlLabel value="3" control={<Radio />} label="Ambos" />
                  <FormControlLabel value="0" control={<Radio />} label="Nenhuma" />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Validade (Dias)"
                  type="number"
                  value={validade}
                  onChange={(e) => setValidade(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Outros">
            <Grid container spacing={2} alignItems="flex-start">
              {!precoPorFilial && filiais.length > 1 && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="filial-estoque-label">Filial do estoque</InputLabel>
                    <Select
                      labelId="filial-estoque-label"
                      label="Filial do estoque"
                      value={estabPrecoId || ''}
                      onChange={(e) => {
                        const id = Number(e.target.value);
                        persistirLimitesNoCache(estabPrecoId);
                        setEstabPrecoId(id);
                        aplicarEstoqueNaTela(estoquesCache, id);
                      }}
                    >
                      {filiais.map((f) => (
                        <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <TextField
                  label={filiais.length > 1 ? 'Estoque atual (filial)' : 'Estoque Atual'}
                  value={fmtQtd(estoqueAtual)}
                  fullWidth
                  onClick={() => {
                    if (!multiEstoque) return;
                    setFiltroFilialEstoque(estabPrecoId || filiais[0]?.id || '');
                    setDlgEstoque(true);
                  }}
                  InputProps={{ readOnly: true }}
                  sx={multiEstoque ? {
                    '& .MuiInputBase-root': { cursor: 'pointer' },
                    '& .MuiInputBase-input': { cursor: 'pointer' },
                  } : undefined}
                  helperText={multiEstoque ? 'Soma dos locais. Clique para ver cada um.' : 'Saldo do estoque principal.'}
                />
                {editing && podeReajustarSaldo && (
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={abrirReajuste}
                    disabled={loading}
                    sx={{ mt: 0.5, textTransform: 'none', fontWeight: 700 }}
                  >
                    Reajustar saldo
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Estoque Mínimo"
                  type="number"
                  inputProps={{ step: '0.001' }}
                  value={estoqueMinimo}
                  onChange={(e) => setEstoqueMinimo(e.target.value)}
                  fullWidth
                  helperText=" "
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Estoque Ideal"
                  type="number"
                  inputProps={{ step: '0.001' }}
                  value={estoqueIdeal}
                  onChange={(e) => setEstoqueIdeal(e.target.value)}
                  fullWidth
                  helperText=" "
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Estoque Máximo"
                  type="number"
                  inputProps={{ step: '0.001' }}
                  value={estoqueMaximo}
                  onChange={(e) => setEstoqueMaximo(e.target.value)}
                  fullWidth
                  helperText=" "
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Qtd. para Atacado"
                  type="number"
                  value={qtdAtacado}
                  onChange={(e) => setQtdAtacado(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0 }}
                  helperText=" "
                />
              </Grid>

              {(dataCadastro || dataUpdate) && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={1}>
                    {dataCadastro && (
                      <Chip size="small" variant="outlined" label={`Cadastro: ${new Date(dataCadastro).toLocaleString()}`} />
                    )}
                    {dataUpdate && (
                      <Chip size="small" color="info" variant="outlined" label={`Última alteração: ${new Date(dataUpdate).toLocaleString()}`} />
                    )}
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Section>
        </Grid>

        {/* Coluna direita */}
        <Grid item xs={12} md={4} lg={3}>
          <Box sx={{ ...sectionCardSx, p: 2, mb: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />}
              label="Ativo"
              sx={{ mb: 1 }}
            />
            {filiais.length > 1 && (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={ativoNaFilial}
                    onChange={(e) => setAtivoNaFilial(e.target.checked)}
                  />
                )}
                label="Ativo nesta filial"
                sx={{ mb: 1, display: 'block' }}
              />
            )}
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 210,
                backgroundColor: 'action.hover',
                mb: 1,
              }}
            >
              {imagemBase64 ? (
                <img
                  src={`data:image/*;base64,${imagemBase64}`}
                  alt="Produto"
                  style={{ width: '100%', maxWidth: 260, height: 'auto', borderRadius: 8 }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">Sem imagem</Typography>
              )}
            </Box>
            <Stack spacing={1}>
              <Button variant="outlined" fullWidth component="label" startIcon={<PhotoCamera />}>
                {imagemBase64 ? 'Trocar' : 'Enviar'}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!file.type.startsWith('image/')) {
                      setErrorMessage('Selecione um arquivo de imagem.');
                      return;
                    }
                    if (file.size > MAX_IMAGE_BYTES) {
                      setErrorMessage('A imagem deve ter no máximo 3 MB.');
                      return;
                    }
                    setErrorMessage('');
                    const b64 = await fileToBase64(file);
                    setImagemBase64(b64);
                    e.target.value = '';
                  }}
                />
              </Button>
              {imagemBase64 && (
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<DeleteOutline />}
                  onClick={() => setImagemBase64(null)}
                >
                  Remover
                </Button>
              )}
            </Stack>
          </Box>

          {/* Preços + Margens */}
          <Section title="Preços">
            <Grid container spacing={1}>
              {precoPorFilial && filiais.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel id="filial-preco-label">Filial dos preços</InputLabel>
                    <Select
                      labelId="filial-preco-label"
                      label="Filial dos preços"
                      value={estabPrecoId || ''}
                      onChange={(e) => {
                        const id = Number(e.target.value);
                        persistirLimitesNoCache(estabPrecoId);
                        setEstabPrecoId(id);
                        aplicarPrecosNaTela(precosCache, id, true);
                        aplicarEstoqueNaTela(estoquesCache, id);
                      }}
                    >
                      {filiais.map((f) => (
                        <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Os valores abaixo valem só para esta filial.
                    </FormHelperText>
                  </FormControl>
                </Grid>
              )}
              {!precoPorFilial && filiais.length > 1 && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                    Preço global: ao salvar, o valor é replicado para todas as filiais.
                  </Alert>
                </Grid>
              )}
              {/* Custo */}
              <Grid item xs={12}>
                <TextField
                  label="R$ Custo"
                  value={valorCusto}
                  onChange={(e) => onChangeCusto(e.target.value)}
                  fullWidth
                  inputMode="numeric"
                  helperText={
                    modoMargem === 'markup'
                      ? 'Markup: percentual sobre o custo. Preço = custo × (1 + markup).'
                      : 'Margem: percentual sobre o preço de venda. Preço = custo ÷ (1 − margem).'
                  }
                />
              </Grid>

              {/* Varejo */}
              <Grid item xs={6}>
                <TextField
                  label={modoMargem === 'markup' ? '% Markup Varejo' : '% Margem Varejo'}
                  value={margemVarejo}
                  onChange={(e) => onChangeMargem('varejo', e.target.value)}
                  fullWidth
                  inputMode="decimal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="R$ Varejo"
                  value={valorVarejo}
                  onChange={(e) => onChangePreco('varejo', e.target.value)}
                  fullWidth
                  required
                  inputMode="numeric"
                  error={!!fieldErrors.valorVarejo}
                  helperText={fieldErrors.valorVarejo || ' '}
                />
              </Grid>

              {/* Atacado */}
              <Grid item xs={6}>
                <TextField
                  label={modoMargem === 'markup' ? '% Markup Atacado' : '% Margem Atacado'}
                  value={margemAtacado}
                  onChange={(e) => onChangeMargem('atacado', e.target.value)}
                  fullWidth
                  inputMode="decimal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="R$ Atacado"
                  value={valorAtacado}
                  onChange={(e) => onChangePreco('atacado', e.target.value)}
                  fullWidth
                  inputMode="numeric"
                />
              </Grid>

              {/* Promoção */}
              <Grid item xs={6}>
                <TextField
                  label={modoMargem === 'markup' ? '% Markup Promoção' : '% Margem Promoção'}
                  value={margemPromocional}
                  onChange={(e) => onChangeMargem('promo', e.target.value)}
                  fullWidth
                  inputMode="decimal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="R$ Promoção"
                  value={valorPromocional}
                  onChange={(e) => onChangePreco('promo', e.target.value)}
                  fullWidth
                  inputMode="numeric"
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>

      {/* Ações */}
      <Box
        sx={{
          mt: 2,
          py: 1.5,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 2,
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={fechar}
          disabled={saving}
          sx={{ minWidth: 140, textTransform: 'none', fontWeight: 700 }}
        >
          Cancelar{embedded ? '' : ' (ESC)'}
        </Button>
        <Button
          id="btn-save"
          type="submit"
          variant="contained"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
        >
          Gravar (F10)
        </Button>
      </Box>

      <Dialog open={dlgEstoque} onClose={() => setDlgEstoque(false)} fullWidth maxWidth="sm">
        <DialogTitle>Estoque por local</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Filial"
              value={filtroFilialEstoque === '' || filtroFilialEstoque == null ? '' : String(filtroFilialEstoque)}
              onChange={(e) => setFiltroFilialEstoque(e.target.value)}
              fullWidth
              size="small"
            >
              {filiais.length > 1 && <MenuItem value="">Todas</MenuItem>}
              {filiais.map((f) => (
                <MenuItem key={f.id} value={String(f.id)}>{f.nome}</MenuItem>
              ))}
            </TextField>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {!filtroFilialEstoque && <TableCell>Filial</TableCell>}
                  <TableCell>Local</TableCell>
                  <TableCell align="right">Quantidade</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linhasEstoqueModal.lista.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={filtroFilialEstoque ? 2 : 3} align="center">
                      Nenhum local nesta filial.
                    </TableCell>
                  </TableRow>
                ) : linhasEstoqueModal.lista.map((linha) => (
                  <TableRow key={`${linha.filialId}-${linha.id}`} hover>
                    {!filtroFilialEstoque && <TableCell>{linha.filialNome}</TableCell>}
                    <TableCell>
                      {linha.nome}
                      {linha.principal ? ' (principal)' : ''}
                    </TableCell>
                    <TableCell align="right">{fmtQtd(linha.saldo)}</TableCell>
                  </TableRow>
                ))}
                {linhasEstoqueModal.lista.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={filtroFilialEstoque ? 1 : 2} sx={{ fontWeight: 800 }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      {fmtQtd(linhasEstoqueModal.total)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgEstoque(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dlgReajuste}
        onClose={() => !reajusteBusy && setDlgReajuste(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Reajustar saldo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Define o novo saldo absoluto{multiEstoque ? ' do local' : ' do estoque'} e registra um
              movimento de inventário. Saldo atual:{' '}
              <strong>{fmtQtd(saldoParaReajuste(multiEstoque ? reajusteLocalId : ''))}</strong>
            </Typography>
            {reajusteErro && <Alert severity="error">{reajusteErro}</Alert>}
            {multiEstoque && locaisReajuste.length > 0 && (
              <TextField
                select
                size="small"
                label="Local de estoque"
                value={reajusteLocalId}
                onChange={(e) => {
                  setReajusteLocalId(e.target.value);
                  setReajusteQtd(String(saldoParaReajuste(e.target.value)));
                }}
                fullWidth
              >
                {locaisReajuste.map((l) => (
                  <MenuItem key={l.id} value={String(l.id)}>
                    {l.nome}{l.principal ? ' (principal)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              autoFocus
              size="small"
              type="number"
              label="Nova quantidade"
              value={reajusteQtd}
              onChange={(e) => setReajusteQtd(e.target.value)}
              inputProps={{ min: 0, step: '0.001' }}
              fullWidth
            />
            <TextField
              size="small"
              label="Observação (opcional)"
              value={reajusteObs}
              onChange={(e) => setReajusteObs(e.target.value)}
              inputProps={{ maxLength: 500 }}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setDlgReajuste(false)} disabled={reajusteBusy}>
            Cancelar
          </Button>
          <Button type="button" variant="contained" onClick={confirmarReajuste} disabled={reajusteBusy}>
            {reajusteBusy ? 'Salvando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <CategoryFormDialog
        open={dlgCategoria}
        onClose={() => setDlgCategoria(false)}
        onSaved={async (_msg, entidade) => {
          setDlgCategoria(false);
          const id = entidade?.id;
          await recarregarCategorias(id);
          if (id) await recarregarSubcategorias(id);
        }}
      />
      <SubCategoryFormDialog
        open={dlgSubcategoria}
        categoriaIdInicial={categoriaId}
        onClose={() => setDlgSubcategoria(false)}
        onSaved={async (_msg, entidade) => {
          setDlgSubcategoria(false);
          const catId = entidade?.categoriaId || categoriaId;
          if (catId) await recarregarSubcategorias(catId, entidade?.id);
        }}
      />
      <BrandFormDialog
        open={dlgMarca}
        onClose={() => setDlgMarca(false)}
        onSaved={async (_msg, entidade) => {
          setDlgMarca(false);
          await recarregarMarcas(entidade?.id);
        }}
      />
    </Box>
  );
}

export default FormProduto;
